import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Switch,
  Typography,
  Flex,
  message,
} from "antd";
import { countries } from "../../../../../../shared-components/countries";
import { useSelector, useDispatch } from "react-redux";
import { createEntityAction } from "../../../../../../store/action/admin/entityActions";

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const CreateNewEntityModal = ({ open, onCancel, onCreate }) => {
  const themeDefault = useSelector((state) => state.themeDefault);
  const createEntityState = useSelector((state) => state.createEntityList);
  const dispatch = useDispatch();
  const isLightBg = themeDefault?.info?.themeColor === "default";
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const values = await form.validateFields();

      const entityData = {
        entityId: values.entityId,
        name: values.entityName,
        description: values.description,
        country: values.country,
        dimensionsEnabled: Boolean(values.enableDimensions),
      };

      await dispatch(createEntityAction(entityData));

      message.success("Entity created successfully!");
      form.resetFields();
      onCancel();

      if (onCreate) {
        onCreate(entityData);
      }
    } catch (error) {
      if (error && typeof error === "object" && error.errorFields) {
        if (error.errorFields && error.errorFields.length > 0) {
          const firstError = error.errorFields[0];
          if (firstError.errors && firstError.errors.length > 0) {
            message.error(firstError.errors[0]);
          }
        }
      } else {
        const errorMessage =
          typeof error === "string" ? error : "Failed to create entity";
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space
          style={{
            borderBottom: themeDefault?.info?.newConfig?.cardborder,
            paddingBottom: 8,
            width: "100%",
          }}
        >
          Create New Entity
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      width={560}
      footer={[
        <Flex
          key="footer"
          gap={8}
          style={{
            padding: 16,
            justifyContent: "flex-end",
            borderTop: themeDefault?.info?.newConfig?.cardborder,
          }}
        >
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit}
            loading={loading || createEntityState.loading}
          >
            Create
          </Button>
        </Flex>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          enableDimensions: false,
        }}
      >
        <Form.Item
          label="Entity ID"
          name="entityId"
          rules={[
            { required: true, message: "Please enter entity ID" },
            { min: 3, message: "Entity ID must be at least 3 characters" },
          ]}
        >
          <Input placeholder="Enter entity ID" />
        </Form.Item>

        <Form.Item
          label="Entity Name"
          name="entityName"
          rules={[
            { required: true, message: "Please enter entity name" },
            { min: 2, message: "Entity name must be at least 2 characters" },
          ]}
        >
          <Input placeholder="Enter entity name" />
        </Form.Item>

        <Form.Item
          name="country"
          label={"Country"}
          rules={[
            {
              required: true,
              message: "Enter the Country",
            },
          ]}
        >
          <Select
            style={{
              width: "100%",
            }}
            placeholder="Select your Country"
            showSearch
          >
            {countries.map((item) => (
              <Option key={item} value={item}>
                {item}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Description" name="description">
          <TextArea placeholder="Enter description" rows={3} />
        </Form.Item>

        <Form.Item name="enableDimensions" valuePropName="checked">
          <Flex align="flex-start" gap={8}>
            <Switch />
            <div>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Enable dimensions for this legal entity
              </Text>
            </div>
          </Flex>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            If enabled, the legal entity will use dimensions
          </Text>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateNewEntityModal;
